/**
 * Creating an account.
 *
 * This page used to take a group invitation and hand it to the join screen,
 * because one code did both jobs. It does not any more: an account code admits
 * somebody to the service, a group code admits an existing account to a group,
 * and this is only the first of those. Somebody arriving with a group
 * invitation makes an account here and follows their link afterwards.
 *
 * The code field appears only while the service is closed. Asking for something
 * that is ignored, or leaving out something that turns out to be required, both
 * waste a stranger's first two minutes — so the server is asked which it is
 * rather than guessed at. It still decides on its own; this only changes what
 * the form says.
 */

import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MINIMUM_AGE_YEARS, ageOn } from "@fc/core";
import { errorMessage, trpc } from "../../lib/trpc.js";
import { useAuth } from "../../lib/auth.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { Logo } from "../../components/ui/Logo.js";
import "./auth.css";

/** Suggests a handle from the name, so nobody has to invent one under pressure. */
function suggestUsername(first: string, last: string): string {
  const base = `${first}${last}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
  return base.slice(0, 20);
}

export function RegisterPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/register" });
  const mode = useQuery(trpc.auth.registrationMode.queryOptions());

  /* Unticked to begin with, and never pre-ticked: consent that was already
     given on your behalf is not consent. */
  const [accepted, setAccepted] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(search.code ?? "");

  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const effectiveUsername = usernameTouched
    ? username
    : suggestUsername(firstName, lastName);

  function validate(): Record<string, string> {
    const problems: Record<string, string> = {};
    if (!firstName.trim()) problems.firstName = "This is required.";
    if (!lastName.trim()) problems.lastName = "This is required.";
    if (!effectiveUsername) problems.username = "Choose a username.";
    if (password.length < 10) problems.password = "Use at least 10 characters.";

    if (!dateOfBirth) {
      problems.dateOfBirth = "This is required.";
    } else if (ageOn(dateOfBirth, new Date()) < MINIMUM_AGE_YEARS) {
      // Said in full rather than as a bare rejection: somebody typing the year
      // wrongly deserves to know which rule they hit.
      problems.dateOfBirth = `You need to be at least ${String(MINIMUM_AGE_YEARS)} to sign up.`;
    }
    return problems;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const problems = validate();
    setFieldErrors(problems);
    if (Object.keys(problems).length > 0) return;

    /* Checked after the field validation, so somebody who has both a bad
       password and an unticked box is told about the password first — the
       box is the last thing they will do, not the first. */
    if (!accepted) {
      setError(
        "Please accept the terms of service and privacy policy to continue.",
      );
      return;
    }

    setBusy(true);
    try {
      await auth.register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth,
        email: email.trim().toLowerCase(),
        username: effectiveUsername,
        password,
        displayName: firstName.trim(),
        code: code.trim() || undefined,
      });
      // Signed in, and belonging to nothing yet — which is what the home screen
      // is written to handle.
      await navigate({ to: "/" });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fc-auth">
      <form
        className="fc-auth__card"
        onSubmit={(event) => void onSubmit(event)}
      >
        <Logo size={28} withWordmark className="fc-auth__logo" />
        <h1 className="fc-auth__title">Create your account</h1>

        {error && (
          <div className="fc-auth__error" role="alert">
            {error}
          </div>
        )}

        <div className="fc-auth__row">
          <Field
            label="First name"
            error={fieldErrors.firstName}
            htmlFor="fc-first"
          >
            <Input
              id="fc-first"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              required
            />
          </Field>
          <Field
            label="Last name"
            error={fieldErrors.lastName}
            htmlFor="fc-last"
          >
            <Input
              id="fc-last"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              required
            />
          </Field>
        </div>

        <Field
          label="Date of birth"
          error={fieldErrors.dateOfBirth}
          hint={`Used only to check you are ${String(MINIMUM_AGE_YEARS)} or over.`}
          htmlFor="fc-dob"
        >
          <Input
            id="fc-dob"
            type="date"
            value={dateOfBirth}
            onChange={(event) => setDateOfBirth(event.target.value)}
            autoComplete="bday"
            required
          />
        </Field>

        <Field
          label="Email"
          hint="We send a link to confirm it."
          error={fieldErrors.email}
          htmlFor="fc-email"
        >
          <Input
            id="fc-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </Field>

        <Field
          label="Username"
          hint="How you appear to other people in a group."
          error={fieldErrors.username}
          htmlFor="fc-username"
        >
          <Input
            id="fc-username"
            value={effectiveUsername}
            onChange={(event) => {
              setUsernameTouched(true);
              setUsername(event.target.value);
            }}
            autoComplete="username"
            spellCheck={false}
            required
          />
        </Field>

        <Field
          label="Password"
          hint="At least 10 characters. A phrase you will remember beats a short puzzle."
          error={fieldErrors.password}
          htmlFor="fc-password"
        >
          <Input
            id="fc-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        {/*
          Shown unless we have been told it is unnecessary — not hidden unless
          we have been told it is needed.

          The difference matters when the query fails: an older API, an offline
          moment, a deploy in progress. Hiding the field then produces a form
          that submits and is rejected server-side with "that invite code isn't
          valid", about a field the person was never shown. Defaulting the other
          way costs somebody one unnecessary field on the day the service opens.
        */}
        {(mode.data?.codeRequired ?? true) && (
          <Field
            label="Access code"
            hint="MyFriends.Cloud is invite-only for now."
            htmlFor="fc-code"
          >
            <Input
              id="fc-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="ABCDE-FGHIJ-KLMNO"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              required
            />
          </Field>
        )}

        {/*
          Above the button rather than beside it, and a real checkbox rather
          than a line of small print saying that pressing the button means
          you agree. Somebody should have to do something to accept.
        */}
        <label className="fc-auth__accept">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
          />
          <span>
            I have read and accept the{" "}
            <Link to="/terms" target="_blank">
              terms of service
            </Link>{" "}
            and the{" "}
            <Link to="/privacy" target="_blank">
              privacy policy
            </Link>
            .
          </span>
        </label>

        {/*
          The button stays enabled. Disabling it would leave somebody pressing
          a dead control with nothing telling them why — the check below says
          what is missing instead.
        */}
        <Button type="submit" loading={busy}>
          Create account
        </Button>

        <p className="fc-auth__footnote">
          Already have an account?{" "}
          <button
            type="button"
            className="fc-auth__link"
            onClick={() =>
              void navigate({ to: "/login", search: { redirect: undefined } })
            }
          >
            Sign in
          </button>
        </p>
      </form>
    </div>
  );
}
